import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  Autocomplete,
  Paper,
  Grid,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  CircularProgress,
  Breadcrumbs,
  Link,
  Card,
  CardContent,
  Dialog,
} from "@mui/material";
import {
  Visibility as VisibilityIcon,
  ArrowBack as ArrowBackIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  createBusinessCase,
  createBusinessCaseInBackground,
  validateFile,
  type IBusinessUnit,
  type IMaterialSubmission,
  type ICreateBusinessCaseData,
  type IDeletedProjectCheckResponse,
  checkDeletedProject,
  permanentlyDeleteBusinessCase,
} from "../../services/businessCaseService";
import MaterialFormTable from "../../components/customGenericComponents/materialForm/MaterialFormTable";
import {
  hasDraft,
  loadDraft,
  saveDraft,
  clearDraft,
  getDraftLastSaved,
  type ICreateBusinessCaseDraft,
} from "../../services/draftStorageService";
import {
  getExcelPreview,
  type IExcelPreview,
  type ISharePointFile,
} from "../../services/sharepointService";
import type { TranslationKeys } from "../../i18n";
import DiscardChangesModal from "../../components/customGenericComponents/modals/DiscardChangesModal";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { useBCT } from "../../hooks/useBCT";
import DeletedProjectWarningModal from "../../components/customGenericComponents/modals/DeletedProjectWarningModal";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <div hidden={value !== index} role="tabpanel">
    {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
  </div>
);

interface CreateBusinessCaseFormData {
  businessCaseName: string;
  sharepointFile: ISharePointFile | null;
  productGpm: string;
  businessUnit: IBusinessUnit | null;
  newMaterialRows: IMaterialSubmission[];
  substitutionMaterialRows: IMaterialSubmission[];
}

const CreateBusinessCasePage: React.FC = () => {
  const { t } = useTranslation<TranslationKeys>();
  const navigate = useNavigate();

  // Form state
  const [formData, setFormData] = useState<CreateBusinessCaseFormData>({
    businessCaseName: "",
    sharepointFile: null,
    productGpm: "",
    businessUnit: null,
    newMaterialRows: [],
    substitutionMaterialRows: [],
  });

  // UI state
  const [sharepointPreviewUrl, setSharepointPreviewUrl] = useState<
    string | null
  >(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [materialTabIndex, setMaterialTabIndex] = useState(0);
  const [showDiscardModal, setShowDiscardModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [_draftInfo, setDraftInfo] = useState<{
    exists: boolean;
    lastSaved: string | null;
  }>({
    exists: false,
    lastSaved: null,
  });

  // Data loading states
  const {
    sharepointFiles,
    sharepointFilesLoading,
    sharepointFilesError,
    businessUnits,
    businessUnitsLoading,
    businessUnitsError,
  } = useBCT();

  // Preview states
  const [excelPreview, setExcelPreview] = useState<IExcelPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  const [deletedProjectCheck, setDeletedProjectCheck] = useState<{
    open: boolean;
    data: IDeletedProjectCheckResponse | null;
    processing: boolean;
  }>({
    open: false,
    data: null,
    processing: false,
  });

  const { refreshSharepointFiles } = useBCT();

  const snapshotEntries = Object.entries(excelPreview?.snapshots || {});

  const handleOpen = (index: number) => {
    setCurrentIndex(index);
    setLightboxOpen(true);
  };

  const handleClose = () => setLightboxOpen(false);

  const handlePrev = () =>
    setCurrentIndex((prev) =>
      prev > 0 ? prev - 1 : snapshotEntries.length - 1
    );

  const handleNext = () =>
    setCurrentIndex((prev) =>
      prev < snapshotEntries.length - 1 ? prev + 1 : 0
    );
  // Load initial data and draft
  useEffect(() => {
    checkForDraft();
  }, []);

  // Track unsaved changes
  useEffect(() => {
    const hasChanges =
      formData.businessCaseName.trim() !== "" ||
      formData.sharepointFile !== null ||
      formData.productGpm.trim() !== "" ||
      formData.businessUnit !== null ||
      formData.newMaterialRows.length > 0 ||
      formData.substitutionMaterialRows.length > 0;

    setHasUnsavedChanges(hasChanges);
  }, [formData]);

  const checkForDraft = () => {
    const draftExists = hasDraft();
    const lastSaved = getDraftLastSaved();
    setDraftInfo({ exists: draftExists, lastSaved });

    if (draftExists) {
      const draft = loadDraft();
      if (draft) {
        setFormData({
          businessCaseName: draft.businessCaseName,
          sharepointFile: draft.sharepointFile,
          productGpm: draft.productGpm,
          businessUnit: draft.businessUnit,
          newMaterialRows: [],
          substitutionMaterialRows: [],
        });
      }
    }
  };

  useEffect(() => {
    if (businessUnitsError) {
      setErrors((prev) => ({ ...prev, businessUnit: businessUnitsError }));
    }
  }, [businessUnitsError]);

  const handleInputChange = (
    field: keyof CreateBusinessCaseFormData,
    value: any
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleFileSelection = async (file: ISharePointFile | null) => {
    handleInputChange("sharepointFile", file);
    setShowPreview(false);
    setExcelPreview(null);
    setPreviewError(null);
    setPreviewLoading(false);
  };

const handlePreviewClick = async () => {
  if (!formData.sharepointFile) return;

  setPreviewLoading(true);
  setPreviewError(null);
  setShowPreview(false);
  setSharepointPreviewUrl(null);
  setExcelPreview(null);

  try {
    const data = await getExcelPreview(formData.sharepointFile);
    const snapshots = data?.data?.snapshots;
    const previewUrl = data?.data?.sharepoint_preview_url;

    if (snapshots && Object.keys(snapshots).length > 0) {
      // Show snapshots
      setExcelPreview(data.data);
      setShowPreview(true);
    } else {
      // Show SharePoint button
      setSharepointPreviewUrl(previewUrl || formData.sharepointFile.webUrl);
      setShowPreview(true);
    }
  } catch (error) {
    // Always fallback to SharePoint URL on any error
    console.log("Using SharePoint preview as fallback");
    setSharepointPreviewUrl(formData.sharepointFile.webUrl);
    setShowPreview(true);
  } finally {
    setPreviewLoading(false);
  }
};

  const handleNewMaterialRowsChange = (rows: IMaterialSubmission[]) => {
    handleInputChange("newMaterialRows", rows);
  };

  const handleSubstitutionMaterialRowsChange = (
    rows: IMaterialSubmission[]
  ) => {
    handleInputChange("substitutionMaterialRows", rows);
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.businessCaseName.trim()) {
      newErrors.businessCaseName = "Business case name is required";
    }

    if (!formData.sharepointFile) {
      newErrors.sharepointFile = "SharePoint Excel file is required";
    }

    if (!formData.productGpm.trim()) {
      newErrors.productGpm = "Product GPM is required";
    }

    if (!formData.businessUnit) {
      newErrors.businessUnit = "Business unit is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSaveAsDraft = async () => {
    const draftData: ICreateBusinessCaseDraft = {
      businessCaseName: formData.businessCaseName,
      sharepointFile: formData.sharepointFile,
      productGpm: formData.productGpm,
      businessUnit: formData.businessUnit,
      assignNewMaterials: true, // Always true now
      substitutionMaterials: true, // Always true now
      materialSubmissions: [], // Convert material rows to submissions if needed
      lastSaved: new Date().toISOString(),
    };
    saveDraft(draftData);
    setDraftInfo({ exists: true, lastSaved: draftData.lastSaved });
    setHasUnsavedChanges(false);
  };

  const handleDeletedProjectConfirm = async () => {
    if (!deletedProjectCheck.data?.bc_id) return;

    try {
      setDeletedProjectCheck((prev) => ({ ...prev, processing: true }));

      // Permanently delete the old business case
      await permanentlyDeleteBusinessCase(
        deletedProjectCheck.data.bc_id.toString()
      );

      toast.success(t('toast.previous_bc_deleted'));
      
      const deletedData = deletedProjectCheck.data;
      
      setDeletedProjectCheck({
        open: false,
        data: null,
        processing: false,
      });

      // Now proceed with actual submission, passing the deleted project data
      await proceedWithSubmit(deletedData);
    } catch (error: any) {
      // Check if it's a user cancellation - already handled in proceedWithSubmit
      if (error.name === "AbortError" || error.message?.includes("cancelled") || error.message?.includes("User cancelled")) {
        setDeletedProjectCheck((prev) => ({ ...prev, processing: false }));
        return;
      }
      toast.error(
        error?.response?.data?.error || t('toast.failed_delete_previous_bc')
      );
      setDeletedProjectCheck((prev) => ({ ...prev, processing: false }));
    }
  };

  const handleDeletedProjectCancel = () => {
    setDeletedProjectCheck({
      open: false,
      data: null,
      processing: false,
    });
    setIsSubmitting(false);
    toast.info(t('toast.create_cancelled_select_different'));
  };

  const proceedWithSubmit = async (deletedProjectData?: IDeletedProjectCheckResponse) => {
    try {
      if (!formData.sharepointFile) {
        toast.error(t('toast.select_excel_file'));
        return;
      }

      // Convert MaterialRow[] to IMaterialSubmission[]
      const materialSubmissions: IMaterialSubmission[] = [
        ...formData.newMaterialRows.map((row) => ({
          materialType: "new" as const,
          productFamily: row.productFamily,
          productGroup: row.productGroup,
          productSubGroup: row.productSubGroup,
          material: row.material,
        })),
        ...formData.substitutionMaterialRows.map((row) => ({
          materialType: "substitution" as const,
          productFamily: row.productFamily,
          productGroup: row.productGroup,
          productSubGroup: row.productSubGroup,
          material: row.material,
        })),
      ];

      const submitData: ICreateBusinessCaseData = {
        businessCaseName: formData.businessCaseName,
        sharepointFile: formData.sharepointFile!,
        productGpm: formData.productGpm,
        businessUnit: formData.businessUnit!,
        materialSubmissions: materialSubmissions,
      };

      // Show background toast
      setHasUnsavedChanges(false); // Clear unsaved changes flag
      toast.info(t('toast.creating_in_background'), {
        autoClose: 8000,
      });
      
      // Keep button disabled while processing
      const { taskId } = await createBusinessCaseInBackground(
        submitData, 
        (progress) => {
          // Live progress updates
          if (progress.status === 'completed') {
            clearDraft();
            toast.success(t('toast.created_successfully', { name: formData.businessCaseName }), {
              autoClose: 6000,
            });
            
            setIsSubmitting(false); // Re-enable button on success
            
            // Refresh dashboard data without navigation
            window.dispatchEvent(new CustomEvent('business-case-created'));
          } else if (progress.status === 'failed' || progress.status === 'timeout') {
            // Only show toast if it's NOT a user cancellation (those are handled separately)
            if (!progress.error?.includes('cancelled') && !progress.error?.includes('User cancelled')) {
              toast.error(progress.error || t('toast.create_failed'));
            }
            setIsSubmitting(false); // Re-enable button on error
            setHasUnsavedChanges(true); // Restore unsaved changes flag on error
          }
        },
        deletedProjectData // Pass the deleted project data if already checked
      );

    } catch (error: any) {
      // Check if it's a user cancellation - show info toast only once
      if (error.name === "AbortError" || error.message?.includes("cancelled") || error.message?.includes("User cancelled")) {
        toast.info(t('toast.create_cancelled'));
        setIsSubmitting(false);
        setHasUnsavedChanges(true);
        return;
      }

      console.error("Error creating business case:", error);

      const errorMessage =
        error?.response?.data?.error ||
        error.message ||
        t('toast.create_failed');

      toast.error(errorMessage);
      setIsSubmitting(false);
      setHasUnsavedChanges(true); // Restore unsaved flag on error
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      toast.error(t('toast.fix_errors_before_submit'));
      return;
    }

    if (!formData.sharepointFile) {
      toast.error(t('toast.select_excel_file'));
      return;
    }

    setIsSubmitting(true);

    // Proceed with submit immediately - background task will handle all checks
    await proceedWithSubmit();
  };

  const handleBackClick = () => {
    if (hasUnsavedChanges) {
      setShowDiscardModal(true);
    } else {
      navigate("/dashboard");
    }
  };

  const handleMaterialTabChange = (
    _: React.SyntheticEvent,
    newValue: number
  ) => {
    setMaterialTabIndex(newValue);
  };

  const handleDiscard = () => {
    clearDraft();
    navigate("/dashboard");
  };

  const handleSaveAsDraftAndExit = async () => {
    await handleSaveAsDraft();
    navigate("/dashboard");
  };

  const handleContinueEditing = () => {
    setShowDiscardModal(false);
  };

  const initialFormState: CreateBusinessCaseFormData = {
    businessCaseName: "",
    sharepointFile: null,
    productGpm: "",
    businessUnit: null,
    newMaterialRows: [],
    substitutionMaterialRows: [],
  };

  const handleResetForm = () => {
    setFormData(initialFormState);
    setErrors({});
    setMaterialTabIndex(0);
    setShowPreview(false);
    setExcelPreview(null);
    setPreviewError(null);
    setPreviewLoading(false);
    setHasUnsavedChanges(false);
    clearDraft();
    setDraftInfo({ exists: false, lastSaved: null });
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const formatLastModified = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (error) {
      return dateString;
    }
  };

  return (
    <Box sx={{ px: 2, py: 3, backgroundColor: "#f5f7fa" }}>
      <Box sx={{ mb: 1 }}>
        <Breadcrumbs aria-label="breadcrumb">
          <Link
            underline="hover"
            fontSize="medium"
            component="button"
            variant="body2"
            onClick={() => navigate("/dashboard")}
            sx={{ textDecoration: "none", color: "text.secondary" }}
          >
            {t("dashboard")}
          </Link>
          <Typography fontSize="medium" variant="body2" color="text.primary">
            {t("create_business_case")}
          </Typography>
        </Breadcrumbs>
      </Box>

      {/* Header Section */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <IconButton onClick={handleBackClick} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5" fontWeight={600}>
            {t("create_business_case")}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", gap: 2 }}>
          <Button
            variant="outlined"
            onClick={handleResetForm}
            disabled={isSubmitting}
            sx={{ textTransform: "none" }}
          >
            {t("reset")}
          </Button>
          <Button
            variant="outlined"
            onClick={handleSaveAsDraft}
            disabled={isSubmitting}
            sx={{ textTransform: "none" }}
          >
            {t("save_as_draft")}
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={isSubmitting}
            startIcon={
              isSubmitting ? <CircularProgress size={20} /> : undefined
            }
            sx={{
              backgroundColor: "#c4262e",
              textTransform: "none",
              "&:hover": { backgroundColor: "#a01e24" },
            }}
          >
            {isSubmitting ? t("creating") : t("create_business_case")}
          </Button>
        </Box>
      </Box>

      {/* Business Case Information Container */}
      <Paper
        elevation={1}
        sx={{
          p: 3,
          mb: 2,
          borderRadius: 3,
          backgroundColor: "#ffffff",
          border: "1px solid #e0e0e0",
        }}
      >
        <Typography
          variant="h6"
          sx={{ mb: 3, fontWeight: 600, color: "#e04a50" }}
        >
          {t("business_case_information")}
        </Typography>

        <Box sx={{ display: "flex", gap: 3, pr: 1 }}>
          {/* Left Side: Input Fields (68%) */}
          <Box sx={{ flex: "0 0 68%" }}>
            <Grid container spacing={2}>
              {/* First Row: BC Name, GPM, BU */}
              <Grid size={4}>
                <TextField
                  fullWidth
                  label={t("business_case_name")}
                  value={formData.businessCaseName}
                  onChange={(e) =>
                    handleInputChange("businessCaseName", e.target.value)
                  }
                  error={!!errors.businessCaseName}
                  helperText={errors.businessCaseName}
                  size="small"
                />
              </Grid>
              <Grid size={4}>
                <TextField
                  fullWidth
                  label={t("product_gpm")}
                  value={formData.productGpm}
                  onChange={(e) =>
                    handleInputChange("productGpm", e.target.value)
                  }
                  error={!!errors.productGpm}
                  helperText={errors.productGpm}
                  size="small"
                />
              </Grid>
              <Grid size={4}>
                <Autocomplete
                  options={businessUnits || []}
                  getOptionLabel={(option) => option.name}
                  value={formData.businessUnit}
                  onChange={(_, newValue) =>
                    handleInputChange("businessUnit", newValue)
                  }
                  loading={businessUnitsLoading}
                  size="small"
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t("business_unit")}
                      error={!!errors.businessUnit}
                      helperText={errors.businessUnit}
                    />
                  )}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                />
              </Grid>

              {/* Second Row: SharePoint File and Preview Button */}
              <Grid size={12}>
                <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                  <Box sx={{ flex: 1 }}>
                    <Autocomplete
                      options={sharepointFiles}
                      getOptionLabel={(option) => option.name}
                      value={formData.sharepointFile}
                      onChange={(_, newValue) => handleFileSelection(newValue)}
                      loading={sharepointFilesLoading}
                      size="small"
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={t("select_sharepoint_excel_file")}
                          error={!!errors.sharepointFile}
                          helperText={errors.sharepointFile}
                        />
                      )}
                      renderOption={(props, option) => (
                        <li {...props} key={option.id}>
                          <Box sx={{ width: "100%" }}>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {option.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Size: {formatFileSize(option.size)} • Modified:{" "}
                              {formatLastModified(option.lastModified)}
                            </Typography>
                          </Box>
                        </li>
                      )}
                      filterOptions={(options, { inputValue }) =>
                        options.filter((option) =>
                          option.name
                            .toLowerCase()
                            .includes(inputValue.toLowerCase())
                        )
                      }
                      noOptionsText={t("no_files_found")}
                      isOptionEqualToValue={(option, value) =>
                        option.id === value.id
                      }
                    />
                  </Box>
                  <Tooltip title={t("preview")}>
                    <span>
                      <Button
                        onClick={handlePreviewClick}
                        disabled={!formData.sharepointFile || previewLoading}
                        sx={{
                          backgroundColor: "#c4262e",
                          color: "white",
                          textTransform: "none",
                          fontSize: "0.875rem",
                          minWidth: "90px",
                          height: "40px",
                          "&:hover": {
                            backgroundColor: "#a01e24",
                          },
                          "&:disabled": {
                            backgroundColor: "#f5c6c3",
                            color: "#fff",
                          },
                        }}
                      >
                        {t("preview")}
                      </Button>
                    </span>
                  </Tooltip>
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* Right Side: Preview Container (32%) */}
          <Box sx={{ flex: "0 0 32%" }}>
            <Box
              sx={{
                minHeight: 200,
                height: "100%",
                border: "2px dashed #e0e0e0",
                borderRadius: 2,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                p: 2,
                backgroundColor: "#fafafa",
              }}
            >
              {previewLoading ? (
                <Box sx={{ textAlign: "center" }}>
                  <CircularProgress size={40} sx={{ mb: 2 }} />
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    {t("loading_preview_please_wait")}
                  </Typography>
                </Box>
              ) : previewError ? (
                <Box sx={{ textAlign: "center" }}>
                  <Typography variant="body1" color="error" sx={{ mb: 1 }}>
                    {previewError}
                  </Typography>
                </Box>
              ) : sharepointPreviewUrl ? (
                <Box sx={{ textAlign: "center" }}>
                  <Typography variant="h4" sx={{ mb: 2 }}>
                    {t("snapshot_not_available")}
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    href={sharepointPreviewUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ textTransform: "none" }}
                  >
                    {t("view_in_sharepoint")}
                  </Button>
                </Box>
              ) : formData.sharepointFile && !showPreview ? (
                <Box sx={{ textAlign: "center" }}>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    {t("file_details")} :
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong> {t("size")} :</strong>{" "}
                    {formatFileSize(formData.sharepointFile.size)}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>{t("last_modified")} :</strong>{" "}
                    {formatLastModified(formData.sharepointFile.lastModified)}
                  </Typography>
                  <Typography variant="body2">
                    <strong> Last Modified By:</strong>{" "}
                    {formData.sharepointFile.lastModifiedBy || "N/A"}
                  </Typography>
                </Box>
              ) : showPreview && excelPreview ? (
                <Box sx={{ width: "100%", overflowY: "auto", maxHeight: 200 }}>
                  <Typography variant="h5" sx={{ mb: 2, textAlign: "center" }}>
                    {t("preview_snapshots")}
                  </Typography>

                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 3,
                      justifyContent: "center",
                    }}
                  >
                    {Object.entries((excelPreview as any).snapshots || {}).map(
                      ([sheetName, url], index) => (
                        <Card
                          key={index}
                          sx={{
                            width: 160,
                            height: 120,
                            cursor: "pointer",
                            display: "flex",
                            flexDirection: "column",
                            "&:hover": { boxShadow: 3 },
                          }}
                          onClick={() => handleOpen(index)}
                        >
                          {/* Image preview */}
                          <Box
                            component="img"
                            src={url as string}
                            alt={`${sheetName} snapshot`}
                            sx={{
                              width: "100%",
                              height: 80,
                              objectFit: "contain",
                              borderBottom: "1px solid #eee",
                            }}
                          />

                          {/* Caption */}
                          <CardContent sx={{ p: 0.5, textAlign: "center" }}>
                            <Typography
                              variant="caption"
                              sx={{ fontSize: "15px" }}
                              noWrap
                            >
                              {sheetName}
                            </Typography>
                          </CardContent>
                        </Card>
                      )
                    )}
                  </Box>

                  {/* Lightbox Dialog */}
                  <Dialog
                    open={lightboxOpen}
                    onClose={handleClose}
                    fullWidth
                    maxWidth="xl"
                    PaperProps={{
                      sx: { position: "relative", bgcolor: "black" },
                    }}
                  >
                    {/* Close Button */}
                    <IconButton
                      onClick={handleClose}
                      sx={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        color: "red",
                      }}
                    >
                      <CloseIcon />
                    </IconButton>

                    {/* Prev Button */}
                    <IconButton
                      onClick={handlePrev}
                      sx={{
                        position: "absolute",
                        top: "50%",
                        left: 8,
                        color: "red",
                      }}
                    >
                      <ArrowBackIosNewIcon />
                    </IconButton>

                    {/* Next Button */}
                    <IconButton
                      onClick={handleNext}
                      sx={{
                        position: "absolute",
                        top: "50%",
                        right: 8,
                        color: "red",
                      }}
                    >
                      <ArrowForwardIosIcon />
                    </IconButton>

                    {/* Image */}
                    <Box
                      component="img"
                      src={
                        Object.values((excelPreview as any).snapshots || {})[
                          currentIndex
                        ] as string
                      }
                      alt={`Preview ${currentIndex + 1}`}
                      sx={{
                        width: "100%",
                        maxHeight: "80vh",
                        objectFit: "contain",
                      }}
                    />
                  </Dialog>
                </Box>
              ) : (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ textAlign: "center" }}
                >
                  {t("no_file_selected_preview_will_appear_here")}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Material Submissions Container */}
      <Paper
        elevation={1}
        sx={{
          p: 3,
          mb: 2,
          borderRadius: 3,
          backgroundColor: "#ffffff",
          border: "1px solid #e0e0e0",
        }}
      >
        <Typography
          variant="h6"
          sx={{ mb: 3, fontWeight: 600, color: "#e04a50" }}
        >
          {t("material_submissions")}
        </Typography>

        <Tabs
          value={materialTabIndex}
          onChange={handleMaterialTabChange}
          textColor="primary"
          indicatorColor="primary"
        >
          <Tab label={t("new_materials")} />
          <Tab label={t("substitution_materials")} />
        </Tabs>

        <TabPanel value={materialTabIndex} index={0}>
          <MaterialFormTable
            rows={formData.newMaterialRows}
            onChange={handleNewMaterialRowsChange}
          />
        </TabPanel>

        <TabPanel value={materialTabIndex} index={1}>
          <MaterialFormTable
            rows={formData.substitutionMaterialRows}
            onChange={handleSubstitutionMaterialRowsChange}
          />
        </TabPanel>
      </Paper>

      <DeletedProjectWarningModal
        open={deletedProjectCheck.open}
        projectName={deletedProjectCheck.data?.project_name || ""}
        deletedAt={deletedProjectCheck.data?.deleted_at || ""}
        deletedByName={deletedProjectCheck.data?.deleted_by_name || ""}
        loading={deletedProjectCheck.processing}
        onConfirm={handleDeletedProjectConfirm}
        onCancel={handleDeletedProjectCancel}
      />

      <DiscardChangesModal
        open={showDiscardModal}
        onClose={handleContinueEditing}
        onDiscard={handleDiscard}
        onSaveAsDraft={handleSaveAsDraftAndExit}
        loading={false}
        onContinueEditing={handleContinueEditing}
      />
    </Box>
  );
};

export default CreateBusinessCasePage;
